/* eslint-disable node/prefer-global/process */
import type { AnnouncementCar } from '@autobid/ui/types/Car'
import type { UseQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useCurrentCarAnnouncementSocket } from '@autobid/ui/composables/car/sockets/useCurrentCarAnnouncementSocket'
import { prepareMultiErrorCause } from '@autobid/ui/utils/auctions/prepareMultiErrorCause'
import { generateImageSizesFromObject } from '@autobid/ui/utils/car/generateImageSizes'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

// #region contants
const CACHE_TIME = 1000 * 60 * 60
// #endregion

// #region internal functions
function prepareCarData(data: AnnouncementCar): AnnouncementCar {
  return {
    ...data,
    imageGroups: generateImageSizesFromObject('hd', data.imageGroups),
  }
}

function useFetchAnnouncementCar() {
  const { $trpc } = useNuxtApp()
  const { locale } = useI18n()

  const fetchAnnouncementCar = async (
    auctionId: number,
  ): Promise<AnnouncementCar | null> => {
    const result = await $trpc.auctions.getCurrentCarAnnouncement.query({ auctionId, lang: locale.value })

    if (!result) {
      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    if ('errors' in result) {
      throw createError({
        statusCode: result.errors[0].status_code,
        statusMessage: result.errors[0].message,
        cause: prepareMultiErrorCause(result.errors),
      })
    }

    if (!result.data.singleItem.item) {
      return null
    }

    return prepareCarData(result.data.singleItem.item.itemData)
  }

  return { fetchAnnouncementCar }
}
// #endregion

export const announcementCarQueryKey = {
  all: () => ['current-car-announcement'] as const,
  byAuctionId: (auctionId: number) => ['current-car-announcement', auctionId] as const,
}

export function usePrefetchAnnouncementCar() {
  const { fetchAnnouncementCar } = useFetchAnnouncementCar()
  const queryClient = useQueryClient()
  const { init: initSockets } = useCurrentCarAnnouncementSocket()
  onMounted(initSockets)

  const prefetchAnnouncementCar = async (
    auctionId: number,
  ): Promise<ReturnType<typeof fetchAnnouncementCar>> => {
    await queryClient.prefetchQuery({
      queryKey: announcementCarQueryKey.byAuctionId(auctionId),
      queryFn: async () => {
        try {
          return await fetchAnnouncementCar(auctionId)
        }
        catch {
          return null
        }
      },
      staleTime: CACHE_TIME,
      ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    })

    return queryClient.getQueryData(announcementCarQueryKey.byAuctionId(auctionId)) || null
  }

  return { prefetchAnnouncementCar }
}

/**
 * Basic current car info that have only thumbnail and few fields that are required to display timer
 */
export function useCurrentCarAnnouncement(auctionId: Ref<number>, options?: UseQueryOptions<
  AnnouncementCar | null,
  Error
>) {
  const { fetchAnnouncementCar } = useFetchAnnouncementCar()
  const { init: initSockets } = useCurrentCarAnnouncementSocket()
  onMounted(initSockets)

  return useQuery({
    queryKey: computed(() =>
      announcementCarQueryKey.byAuctionId(unref(auctionId)),
    ),
    queryFn: async () => {
      try {
        return await fetchAnnouncementCar(unref(auctionId))
      }
      catch (error) {
        const { message, code, handled } = getError(error)

        if (!handled) {
          throw createError({ statusCode: code, statusMessage: message })
        }

        return null
      }
    },
    staleTime: CACHE_TIME,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    refetchOnWindowFocus: false,
    ...(options || {}),
  })
}
